import React from "react";
import { Check, X } from "lucide-react";
import { COLLAPSED_ROW_COPY } from "./CollapsedSectionRow.copy";
import {
  ChangeButton,
  Root,
  StatusMark,
  StatusSpacer,
  Summary,
  Title,
} from "./CollapsedSectionRow.styles";
import type { CollapsedSectionRowProps, CollapsedSectionStatus } from "./CollapsedSectionRow.types";

const STATUS_ICON: Record<Exclude<CollapsedSectionStatus, "none">, React.ReactNode> = {
  done: <Check size={10} strokeWidth={4} aria-hidden="true" />,
  attention: <span aria-hidden="true">!</span>,
  blocked: <X size={10} strokeWidth={4} aria-hidden="true" />,
};

/** The round status mark on its own (an open section's heading uses it too). */
export const SectionStatusMark: React.FC<{ status: Exclude<CollapsedSectionStatus, "none">; label?: string }> = ({ status, label }) => (
  <StatusMark $status={status} role="img" aria-label={label ?? COLLAPSED_ROW_COPY.statusLabels[status]}>
    {STATUS_ICON[status]}
  </StatusMark>
);

/**
 * A finished section folded to one line: status mark, title, one-line summary, and a Change action.
 * Stateless. The Change button is the keyboard stop (Enter/Space); a click anywhere on the row
 * does the same for the mouse. The whole row shows a focus ring while the button has focus.
 */
export const CollapsedSectionRow: React.FC<CollapsedSectionRowProps> = ({
  title,
  summary,
  status = "done",
  statusLabel,
  actionLabel = COLLAPSED_ROW_COPY.actionLabel,
  onChange,
  className,
}) => (
  <Root className={className} role="group" aria-label={title} onClick={onChange}>
    {status === "none" ? (
      <StatusSpacer aria-hidden="true" />
    ) : (
      <SectionStatusMark status={status} label={statusLabel} />
    )}
    <Title>{title}</Title>
    <Summary title={summary}>{summary}</Summary>
    <ChangeButton
      type="button"
      aria-label={`${actionLabel} ${title}`}
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
    >
      {actionLabel}
    </ChangeButton>
  </Root>
);

export default CollapsedSectionRow;
